import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SearchBar from "./SearchBar";

// Shared config
const mainMenuItems = [
  { id: "gallery", title: "Gallery", route: "/pages/galleries" },
  { id: "size-guide", title: "Size Guide", route: "/pages/size-guide" },
  { id: "lookbook", title: "Lookbook", route: "/lookbook" },
];

const shopDropdownItems = [
  { id: "all", title: "All", filter: "all" },
  { id: "bras", title: "Bras", filter: "bras" },
  { id: "knickers", title: "Knicker Drawer", filter: "knickers" },
  { id: "swim", title: "Swim", filter: "swim" },
  { id: "clothing", title: "Clothing", filter: "clothing" },
  { id: "accessories", title: "Accessories", filter: "accessories" },
  { id: "personalised", title: "Personalised", filter: "personalised" },
];

// Map filter keys to Shopify handles
const collectionHandleFromFilter = (filter) => {
  switch (filter) {
    case "all":
      return "all";
    case "bras":
      return "bralettes";
    case "knickers":
      return "knickers";
    case "swim":
      return "swimwear";
    case "clothing":
      return "double-denim-silk-pyjamas";
    case "accessories":
      return "accessories";
    case "personalised":
      return "personalised";
    default:
      return filter;
  }
};

// =======================
// Desktop main menu
// =======================
export function DesktopMainMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const [shopDropdownVisible, setShopDropdownVisible] = useState(false);

  const handleShopClick = (filter) => {
    const handle = collectionHandleFromFilter(filter);
    navigate(`/collections/${handle}`);
    setShopDropdownVisible(false);
  };

  const isShopPath =
    location.pathname === "/collections/all" ||
    location.pathname.startsWith("/collections/") ||
    location.pathname.startsWith("/products/") ||
    location.pathname === "/shop";

  return (
    <ul className="flex justify-center space-x-4 text-xs uppercase tracking-wide">
      <li
        className="relative cursor-pointer"
        onMouseEnter={() => setShopDropdownVisible(true)}
        onMouseLeave={() => setShopDropdownVisible(false)}
      >
        <span
          className={isShopPath ? "italic underline" : ""}
          onClick={() => navigate("/collections/all")}
        >
          Shop
        </span>
        {shopDropdownVisible && (
          <div className="absolute top-full left-0 w-48 bg-white bg-opacity-70 backdrop-blur-md shadow-lg z-50">
            <ul>
              {shopDropdownItems.map((item) => (
                <li
                  key={item.id}
                  className="cursor-pointer px-4 py-2 hover:underline"
                  onClick={() => handleShopClick(item.filter)}
                >
                  {item.title}
                </li>
              ))}
            </ul>
          </div>
        )}
      </li>

      {mainMenuItems.map((item) => (
        <li
          key={item.id}
          className={`cursor-pointer ${
            location.pathname === item.route ? "italic underline" : ""
          }`}
          onClick={() => navigate(item.route)}
        >
          {item.title}
        </li>
      ))}
    </ul>
  );
}

// =======================
// Mobile bar: Search + Shop always visible
// =======================
function MainMenu() {
  const navigate = useNavigate();

  const [shopDropdownVisible, setShopDropdownVisible] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const handleShopClick = (filter) => {
    const handle = collectionHandleFromFilter(filter);
    navigate(`/collections/${handle}`);
    setShopDropdownVisible(false);
    setMobileSearchOpen(false);
  };

  return (
    // Sticky bar just under the navbar, mobile only
    <div className="w-full sticky top-[50px] z-40 bg-white md:hidden">
      {/* Row of actions: Search + Shop (always visible) */}
      <div className="flex items-center gap-3 px-4 pt-2">
        <button
          type="button"
          onClick={() => {
            setMobileSearchOpen((v) => !v);
            setShopDropdownVisible(false);
          }}
          className={`
            flex-1 px-4 py-2 text-xs uppercase tracking-wide
            bg-white text-customNavy
          `}
          aria-expanded={mobileSearchOpen}
          aria-controls="mobile-search-region"
        >
          Search
        </button>

        <div className="relative flex-1">
          <button
            type="button"
            onClick={() => {
              setShopDropdownVisible((v) => !v);
              setMobileSearchOpen(false);
            }}
            className={`
              w-full px-4 py-2 text-xs uppercase tracking-wide
              bg-white text-customNavy
            `}
            aria-expanded={shopDropdownVisible}
            aria-controls="mobile-shop-dropdown"
          >
            Shop
          </button>
        </div>
      </div>

      {/* Shop dropdown */}
      {shopDropdownVisible && (
        <div id="mobile-shop-dropdown" className="w-full mt-1 px-4 pb-2">
          <ul className="w-full flex flex-col items-center text-center space-y-2">
            {shopDropdownItems.map((item) => (
              <li
                key={item.id}
                className="cursor-pointer text-xs uppercase tracking-wide text-customNavy hover:underline"
                onClick={() => handleShopClick(item.filter)}
              >
                {item.title}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Search region */}
      <div
        id="mobile-search-region"
        className="overflow-hidden transition-all duration-200 ease-out "
        style={{ maxHeight: mobileSearchOpen ? 160 : 0 }}
      >
        <div
          className={`mt-3 transform-gpu transition-transform duration-200 ease-out ${
            mobileSearchOpen
              ? "translate-x-0 opacity-100"
              : "-translate-x-4 opacity-0"
          }`}
        >
          <SearchBar
            autoFocus={true}
            onDone={() => {
              setMobileSearchOpen(false);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default MainMenu;
